import React, {useEffect, useState} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import {useTranslation} from 'react-i18next';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import PropTypes from 'prop-types';
import {useShellPortCreate, useShellPortUpdate} from '../../../hooks/useShells';
import Button from '../../core/input/Button';
import {useSetSnackbar} from '../../../hooks/useSnackbar';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import {useIPv4CIDRValidation} from '../../../hooks/useUtils';
import ShellPortProtocolSelector from '../../core/input/ShellPortProtocolSelector';
import {useApiErrorHandler} from '@karpeleslab/klb-react-services';

const knownProtocols = [
	['ssh', 22, 'tcp'],
	['rdp' ,3389, 'tcp'],
	['http' ,80, 'tcp'],
	['https' ,443, 'both'],
	['mysql' ,3306, 'tcp'],
	['postgresql' ,5432, 'tcp'],
	['vnc' ,5900, 'tcp'],
	['dns' ,53, 'both'],
	['telnet' ,23, 'tcp'],
	['ftp' ,21, 'tcp'],
];

const CreateShellPortDialog = ({shell, open, setOpen, refreshList, refreshing, portToUpdate = null}) => {
	const {t} = useTranslation();
	const [createPort, creating] = useShellPortCreate(shell.Shell__);
	const [updatePort, updating] = useShellPortUpdate(portToUpdate?.Shell_Port__);
	const [ ,handleError] = useApiErrorHandler();

	const [port, setPort] = useState(portToUpdate?.Guest_Port ?? null);
	const [name, setName] = useState(portToUpdate?.Name ?? '');
	const [protocol, setProtocol] = useState(portToUpdate?.Protocol ?? 'tcp');
	const [source, setSource] = useState(portToUpdate?.Source ?? null);
	const [enabled, setEnabled] = useState(false);
	const cidrValidation = useIPv4CIDRValidation();

	const [useKnownProtocol, setUseKnownProtocol] = useState(true);
	const [knownProtocol, setKnownProtocol] = useState(knownProtocols[0][0]);

	const setSnackbar = useSetSnackbar();

	useEffect(() => {
		if(!!source && !cidrValidation(source)) {
			setEnabled(false);
			return;
		}

		if(useKnownProtocol && !!knownProtocol) {
			setEnabled(true);
			return;
		}

		if(((name ?? '').trim().length > 0 && port && port >= 0 && port <= 65535)) {
			setEnabled(true);
		}else {
			setEnabled(false);
		}
	}, [port, name, source, knownProtocol, useKnownProtocol]);

	const reset = () => {
		setPort(portToUpdate?.Guest_Port ?? null);
		setName(portToUpdate?.Name ?? '');
		setSource(portToUpdate?.Source ?? null);
		setProtocol(portToUpdate?.Protocol ?? 'tcp');
		setUseKnownProtocol(!portToUpdate);
		setEnabled(!portToUpdate);
		setKnownProtocol(knownProtocols[0][0]);
	};

	const handleClose = () => {
		reset();
		setOpen(null);
	};

	const handleCreate = e => {
		e.preventDefault();

		if(portToUpdate) {
			updatePort(name, port, protocol, source, { handleError: false })
				.then(() => {
					setSnackbar(t('shell_port_update_success'), 'success');
				})
				.then(() => reset())
				.then(() => refreshList())
				.then(handleClose)
				.catch(handleError);
		}else {
			let n = name;
			let p = port;
			let ptc = protocol;
			let s = source;
			if (useKnownProtocol) {
				knownProtocols.forEach((k) => {
					if(k[0] === knownProtocol) {
						n = k[0];
						p = k[1];
						ptc = k[2];
					}
				});
			}
			createPort(n, p, ptc, s, { handleError: false })
				.then(() => {
					setSnackbar(t('shell_port_create_success'), 'success');
				})
				.then(() => reset())
				.then(() => refreshList())
				.then(handleClose)
				.catch((e) => {
					handleError(e, null, null, {reThrow: false});
				});
		}
	};

	return (<Dialog
		open={open}
		aria-labelledby={t('dialog_create_shell_port_key_title')}
		aria-describedby={t('dialog_create_shell_port_key_title')}
		onEnter={() => reset()}
	>
		<DialogTitle id='created-shellport-title'>
			{t('dialog_create_shell_port_key_title')}
		</DialogTitle>
		<DialogContent>
			<Grid container spacing={2}>
				{(!portToUpdate && !useKnownProtocol) && <Grid item xs={12}>
					<TextField
						variant='outlined'
						label={t('tablecell_shell_port_name')}
						required
						fullWidth
						value={name}
						disabled={creating || updating}
						onChange={e => setName(e.target.value)}
					/>
				</Grid>}
				{(!portToUpdate && useKnownProtocol) && <Grid item xs={12}>
					<TextField
						label={t('known_protocol_title')}
						helperText={t('known_protocol_helper')}
						value={knownProtocol}
						onChange={e => {
							if(e.target.value==='manual') {
								setUseKnownProtocol(false);
							}else {
								setKnownProtocol(e.target.value);
							}
						}}
						select
						fullWidth
						disabled={creating  || updating || !!portToUpdate}
						required={true}
						variant='outlined'
						SelectProps={{
							native: true
						}}
					>
						{knownProtocols.map(p =>
							<option value={p[0]} key={p[0]}>{t('known_protocol', {name:p[0], port:p[1], protocol: p[2]})}</option>
						)}

						<option value='manual'>{t('manual_protocol')}</option>
					</TextField>
				</Grid>}
				{(!portToUpdate && !useKnownProtocol) && <Grid item xs={12}>
					<TextField
						variant='outlined'
						label={t('tablecell_shell_port_guest_port')}
						helperText={t('tablecell_shell_port_guest_port_helper')}
						type={'number'}
						InputProps={{ inputProps: { min: 0, max: 65535 } }}
						required
						fullWidth
						value={port}
						disabled={creating  || updating || !!portToUpdate}
						onChange={e => setPort(e.target.value)}
					/>
				</Grid>}
				{!useKnownProtocol && <Grid item xs={12}>
					<ShellPortProtocolSelector value={protocol} setValue={setProtocol} disabled={creating  || updating}/>
				</Grid>}
				<Grid item xs={12}>
					<TextField
						variant='outlined'
						label={t('tablecell_shell_port_source')}
						helperText={t('tablecell_shell_port_source_helper')}
						fullWidth
						value={source}
						disabled={creating  || updating}
						error={!!source && !cidrValidation(source)}
						onChange={e => setSource(e.target.value)}
					/>
				</Grid>
			</Grid>
		</DialogContent>
		<DialogActions>
			<Button
				color='default'
				onClick={handleClose}
				disabled={creating || refreshing  || updating}
			>
				{t('close_btn')}
			</Button>
			<Button
				onClick={handleCreate}
				color='primary'
				variant='contained'
				disabled={creating || refreshing || !enabled  || updating}
				loading={creating || refreshing  || updating}
			>
				{portToUpdate ? t('edit_btn') : t('create_btn')}
			</Button>
		</DialogActions>
	</Dialog>);
};

export default CreateShellPortDialog;

CreateShellPortDialog.propTypes = {
	shell: PropTypes.object,
	portToUpdate: PropTypes.object,
	open: PropTypes.bool.isRequired,
	refreshList: PropTypes.func.isRequired,
	refreshing: PropTypes.bool.isRequired,
	setOpen: PropTypes.func.isRequired
};
